import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@footypicks/shared/context/AuthContext';
import { useCompetitionsContext } from '@footypicks/shared/context/CompetitionsContext';
import { useTheme } from '@footypicks/shared/context/ThemeContext';
import { useOrganizations } from '@footypicks/shared/hooks/useOrganizations';
import { useMetaTags } from '@footypicks/shared/hooks/useMetaTags';
import { useUserCompetitionEnrollment } from '@footypicks/shared/hooks/useUserCompetitionEnrollment';
import { supabase } from '@footypicks/shared/lib/supabase';
import { queryCache, CACHE_TTL } from '@footypicks/shared/lib/cache';
import { LoadingSpinner } from '@footypicks/shared/components/LoadingSpinner';
import { SupportLink } from '@footypicks/shared/components/SupportLink';
import { AuthForm } from '@footypicks/shared/components/AuthForm';
import { GradientHeader } from '@footypicks/shared/components/GradientHeader';
import { SafeAreaCover } from '@footypicks/shared/components/SafeAreaCover';
import { UserProfileModal } from '@footypicks/shared/components/UserProfileModal';
import { MyCompetitionsDashboard } from '@footypicks/shared/components/MyCompetitionsDashboard';
import { PublicCompetitionCard } from '@footypicks/shared/components/PublicCompetitionCard';
import { PullToRefresh } from '@footypicks/shared/components/PullToRefresh';
import { RecentlyCompletedSection } from '@footypicks/shared/components/RecentlyCompletedSection';
import { useRecentlyCompletedCompetitions } from '@footypicks/shared/hooks/useRecentlyCompletedCompetitions';
import { useScrollDirection } from '@footypicks/shared/hooks/useScrollDirection';
import { updateThemeColor } from '@footypicks/shared/utils/format';
import { updateOrganizationBranding } from '@footypicks/shared/utils/favicon';
import { PWAInstallBanner } from '@footypicks/shared/components/PWAInstallBanner';
import { captureMessage, addBreadcrumb } from '@footypicks/shared/lib/sentry';
import { computeFirstGameweekDeadlines } from '@footypicks/shared/lib/publicCompetitionStatus';
import { Trophy, Target, Globe, AlertTriangle } from 'lucide-react';

export function OrganizationLandingPage() {
  const { organizationSlug } = useParams<{ organizationSlug: string }>();
  const navigate = useNavigate();
  const { user, signOut, setCurrentOrganization, loading: authLoading } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { getOrganizationBySlug } = useOrganizations();
  const { competitions, loading: competitionsLoading, error: competitionsError, refreshCompetitions } = useCompetitionsContext();

  const [organization, setOrganization] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lmsCompetitions, setLmsCompetitions] = useState<any[]>([]);
  const [predictorCompetitions, setPredictorCompetitions] = useState<any[]>([]);
  const [worldcupCompetitions, setWorldcupCompetitions] = useState<any[]>([]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [roleLoading, setRoleLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [publicStats, setPublicStats] = useState<Record<string, { total: number; remaining: number | null }>>({});
  const [wcFirstKickoff, setWcFirstKickoff] = useState<Date | null>(null);
  const [publicFirstGameweekDeadlines, setPublicFirstGameweekDeadlines] = useState<Record<string, Date>>({});
  const scrollDirection = useScrollDirection();

  const {
    enrolledCompetitions,
    availableCompetitions,
    firstGameweekDeadlines: enrollmentFirstGameweekDeadlines,
    loading: enrollmentLoading,
    refreshEnrollments
  } = useUserCompetitionEnrollment();

  // Cross-check enrollment against competitions context so inactive comps cannot linger
  // in My Competitions via a stale enrollment cache after an admin deactivates them.
  const activeCompetitionIds = new Set(
    competitions
      .filter(
        (c) =>
          c.isActive === true &&
          c.status !== 'finished' &&
          c.status !== 'completed'
      )
      .map((c) => c.id)
  );
  const filterByActive = !competitionsLoading && competitions.length > 0;
  const visibleEnrolledCompetitions = filterByActive
    ? enrolledCompetitions.filter((c) => activeCompetitionIds.has(c.id))
    : enrolledCompetitions;
  const visibleAvailableCompetitions = filterByActive
    ? availableCompetitions.filter((c) => activeCompetitionIds.has(c.id))
    : availableCompetitions;

  // Fetch recently completed competitions
  const {
    competitions: recentlyCompletedCompetitions,
    loading: recentlyCompletedLoading
  } = useRecentlyCompletedCompetitions(organization?.id);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)');
    setIsMobile(mql.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // Update meta tags when organization changes
  useMetaTags({
    organization: organization
  });

  // Check for password recovery tokens in URL and redirect to reset password page
  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const type = hashParams.get('type');
    const accessToken = hashParams.get('access_token');

    // If this is a password recovery link, redirect to the reset password page
    if (type === 'recovery' && accessToken) {
      console.log('Password recovery detected, redirecting to reset-password page');
      navigate('/reset-password', { replace: true });
      return;
    }
  }, [navigate]);

  // Load organization (separate from competitions)
  useEffect(() => {
    const loadOrganization = async () => {
      if (!organizationSlug) {
        setError('No organization specified');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('Fetching organization with slug:', organizationSlug);
        const org = await getOrganizationBySlug(organizationSlug);
        console.log('Organization result:', org);

        if (!org) {
          console.error('Organization not found for slug:', organizationSlug);
          setError(`Organization "${organizationSlug}" not found`);
          setLoading(false);
          return;
        }

        setOrganization(org);

        // Set in AuthContext so CompetitionsProvider can load competitions
        setCurrentOrganization(org);

        // Immediately update the page title
        document.title = org.name;
        console.log('[OrganizationLandingPage] Set document.title to:', org.name);

        // Apply theme colors immediately
        const primaryColor = org.primary_color || '#4A1D96';
        const secondaryColor = org.secondary_color || '#5B2BA6';
        document.documentElement.style.setProperty('--color-theme-primary', primaryColor);
        document.documentElement.style.setProperty('--color-theme-secondary', secondaryColor);
        updateThemeColor(primaryColor);

        // Update organization-specific branding (favicon, app icons, manifest)
        updateOrganizationBranding({
          organizationName: org.name,
          organizationSlug: org.slug,
          organizationLogoUrl: org.logoUrl,
          primaryColor: primaryColor,
          secondaryColor: secondaryColor
        });

        setLoading(false);
      } catch (err) {
        console.error('Error loading organization:', err);
        setError('Failed to load organization');
        setLoading(false);
      }
    };

    loadOrganization();
  }, [organizationSlug, getOrganizationBySlug, user, setCurrentOrganization]);

  // Filter competitions when they're loaded
  useEffect(() => {
    if (!organization || competitionsLoading) {
      return;
    }

    // Filter active competitions from context (exclude finished/completed competitions)
    const orgCompetitions = competitions.filter(c =>
      c.organizationId === organization.id &&
      c.isActive === true &&
      c.status !== 'finished' &&
      c.status !== 'completed'
    );

    console.log('Filtered competitions from context:', {
      competitions: orgCompetitions,
      allCompetitions: competitions,
      orgId: organization.id
    });

    // Transform to database format for compatibility
    const transformedCompetitions = orgCompetitions.map(c => ({
      ...c,
      organization_id: c.organizationId,
      start_date: c.startDate instanceof Date ? c.startDate.toISOString() : c.startDate,
      end_date: c.endDate ? (c.endDate instanceof Date ? c.endDate.toISOString() : c.endDate) : null,
      competition_type: c.competitionType || 'lms',
      entry_fee_enabled: Boolean(c.entryFeeEnabled),
      entry_fee_amount: c.entryFeeAmount || 0,
      entry_fee_currency: c.entryFeeCurrency || 'gbp',
      is_active: c.isActive
    }));

    const lms = transformedCompetitions.filter(c => c.competition_type === 'lms');
    const predictor = transformedCompetitions.filter(c => c.competition_type === 'predictor');
    const worldcup = transformedCompetitions.filter(c => c.competition_type === 'worldcup');
    console.log('Filtered by type:', { lms, predictor, worldcup });
    setLmsCompetitions(lms);
    setPredictorCompetitions(predictor);
    setWorldcupCompetitions(worldcup);
  }, [organization, competitions, competitionsLoading]);

  // Diagnostics: report real load failures to Sentry. Empty/off-season catalogues
  // are breadcrumbs only so they do not create ongoing issues.
  const sentryReportedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!organization || competitionsLoading) return;
    const reportKey = `${organization.id}:${!!user}:${!!competitionsError}:${competitions.length}`;
    if (sentryReportedRef.current === reportKey) return;

    if (competitionsError) {
      sentryReportedRef.current = reportKey;
      captureMessage('OrganizationLandingPage: competitions failed to load', 'error', {
        organizationId: organization.id,
        organizationSlug: organization.slug,
        authenticated: !!user,
        error: competitionsError
      });
      return;
    }

    const orgComps = competitions.filter(c => c.organizationId === organization.id);
    const activeOrgComps = orgComps.filter(c => c.isActive && c.status !== 'finished' && c.status !== 'completed');
    // Off-season orgs (competitions exist, none currently active) are an empty
    // state, not a production error. Breadcrumb only — do not create Sentry issues.
    if (orgComps.length > 0 && activeOrgComps.length === 0) {
      sentryReportedRef.current = reportKey;
      addBreadcrumb('OrganizationLandingPage: no active competitions visible', 'competitions', 'info', {
        organizationId: organization.id,
        organizationSlug: organization.slug,
        authenticated: !!user,
        totalCompetitionsForOrg: orgComps.length,
        totalCompetitionsInContext: competitions.length
      });
    } else if (activeOrgComps.length > 0) {
      addBreadcrumb('Active competitions resolved', 'competitions', 'info', {
        count: activeOrgComps.length,
        types: Array.from(new Set(activeOrgComps.map(c => c.competitionType)))
      });
    } else {
      addBreadcrumb('Organization has no competitions configured', 'competitions', 'info', {
        organizationId: organization.id,
        organizationSlug: organization.slug,
      });
    }
  }, [organization, competitions, competitionsLoading, competitionsError, user]);

  const getDisplayName = () => {
    if (!user) return '';
    const firstName = user.user_metadata?.first_name;
    const lastName = user.user_metadata?.last_name;
    if (firstName && lastName) return `${firstName} ${lastName}`;
    if (firstName) return firstName;
    return user.email?.split('@')[0] || 'User';
  };

  const handleSignIn = () => {
    setShowAuthModal(true);
  };

  const handleOpenProfile = () => {
    setShowProfileModal(true);
  };

  const handleCloseProfile = () => {
    setShowProfileModal(false);
  };
  // Check if user is admin (cached, uses limit(1) for safety)
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user || !organization) {
        setIsAdmin(false);
        setRoleLoading(false);
        return;
      }

      const cacheKey = queryCache.generateKey('user_admin_role', {
        userId: user.id,
        orgId: organization.id
      });

      try {
        setRoleLoading(true);

        const isUserAdmin = await queryCache.deduplicate<boolean>(
          cacheKey,
          async () => {
            const { data, error } = await supabase
              .from('user_organizations')
              .select('role')
              .eq('user_id', user.id)
              .eq('organization_id', organization.id)
              .eq('access_scope', 'footypicks')
              .limit(1);

            if (error) {
              console.error('Error checking admin status:', error);
              return false;
            }
            return data?.[0]?.role === 'admin';
          },
          CACHE_TTL.LONG // 30 minutes - role rarely changes
        );

        setIsAdmin(isUserAdmin);
      } catch (err) {
        console.error('Error checking admin status:', err);
        setIsAdmin(false);
      } finally {
        setRoleLoading(false);
      }
    };

    checkAdminStatus();
  }, [user, organization]);

  // Public stats: total/remaining entries per active competition (anon-readable RPC)
  const activeIdsKey = [
    ...lmsCompetitions.filter(c => c.is_active).map(c => c.id),
    ...predictorCompetitions.filter(c => c.is_active).map(c => c.id),
    ...worldcupCompetitions.filter(c => c.is_active).map(c => c.id),
  ].join(',');
  useEffect(() => {
    if (user) return;
    const ids = activeIdsKey ? activeIdsKey.split(',') : [];
    if (ids.length === 0) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.rpc('get_public_competition_stats', { _competition_ids: ids });
      if (cancelled || error || !data) return;
      const map: Record<string, { total: number; remaining: number | null }> = {};
      (data as any[]).forEach(r => {
        map[r.competition_id] = { total: r.total_entries ?? 0, remaining: r.remaining_entries };
      });
      setPublicStats(map);
    })();
    return () => { cancelled = true; };
  }, [user, activeIdsKey]);

  // World Cup phase is gated on the actual first match kickoff (not the
  // competition's start_date, which is set to the entries-open date).
  const hasAnyWorldcup = worldcupCompetitions.some(c => c.is_active);
  useEffect(() => {
    if (!hasAnyWorldcup) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('wc_games')
        .select('match_date')
        .eq('stage', 'GROUP_STAGE')
        .order('match_date', { ascending: true })
        .limit(1);
      if (cancelled || error || !data || data.length === 0) return;
      setWcFirstKickoff(new Date(data[0].match_date));
    })();
    return () => { cancelled = true; };
  }, [hasAnyWorldcup]);

  // LMS entries close at GW1 kickoff — fetch for public (logged-out) cards.
  const activeLmsIdsKey = lmsCompetitions.filter(c => c.is_active).map(c => c.id).join(',');
  useEffect(() => {
    if (user) return;
    const activeLms = lmsCompetitions.filter(c => c.is_active);
    if (activeLms.length === 0) {
      setPublicFirstGameweekDeadlines({});
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('competition_games')
        .select('competition_id, games(match_date, gameweek)')
        .in('competition_id', activeLms.map(c => c.id));
      if (cancelled || error || !data) return;
      setPublicFirstGameweekDeadlines(
        computeFirstGameweekDeadlines(data as any, activeLms)
      );
    })();
    return () => { cancelled = true; };
  }, [user, activeLmsIdsKey, lmsCompetitions]);


  // Stale-while-revalidate render gate:
  // Show full-screen spinner only while we lack the organization (need branding/header to render).
  // Once organization is loaded, render the page shell immediately and let the
  // competition lists show their own inline skeletons while enrollment data loads.
  const isInitialLoading = (loading && !organization) || authLoading;

  if (isInitialLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <LoadingSpinner message="Loading..." />
      </div>
    );
  }

  if (error || !organization) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Organization Not Found
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {error || 'The organization you are looking for does not exist.'}
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-gradient-to-r from-theme-primary to-theme-secondary text-white px-6 py-3 rounded-xl font-semibold hover:opacity-90 transition-all"
          >
            Back to Home
          </button>
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <SupportLink variant="block-with-icon" />
          </div>
        </div>
      </div>
    );
  }

  // Active competitions (is_active = true)
  const activeLmsCompetitions = lmsCompetitions.filter(c => c.is_active);
  const activePredictorCompetitions = predictorCompetitions.filter(c => c.is_active);
  const activeWorldcupCompetitions = worldcupCompetitions.filter(c => c.is_active);

  const hasLMS = activeLmsCompetitions.length > 0;
  const hasPredictor = activePredictorCompetitions.length > 0;
  const hasWorldcup = activeWorldcupCompetitions.length > 0;
  const headerHidden = isMobile && scrollDirection === 'down';

  console.log('Competition availability:', {
    hasLMS,
    hasPredictor,
    hasWorldcup,
    activeLmsCount: activeLmsCompetitions.length,
    activePredictorCount: activePredictorCompetitions.length,
    activeWorldcupCount: activeWorldcupCompetitions.length
  });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* User Profile Modal */}
      <UserProfileModal
        isOpen={showProfileModal}
        onClose={handleCloseProfile}
        onSignOut={signOut}
        displayName={getDisplayName()}
        userEmail={user?.email}
      />

      <SafeAreaCover />
      <div className={`fixed top-0 left-0 right-0 z-50 transition-transform duration-300 will-change-transform ${headerHidden ? '-translate-y-full' : 'translate-y-0'}`}>
        <GradientHeader
          organizationName={organization.name}
          organizationLogoUrl={organization.logoUrl}
          subtitle="Choose Your Competition"
          user={user}
          isDark={isDark}
          isAdmin={isAdmin}
          roleLoading={roleLoading}
          displayName={getDisplayName()}
          showBetaBadge={true}
          onToggleTheme={toggleTheme}
          onSignIn={handleSignIn}
          onNavigateHome={() => navigate('/')}
          onNavigateAdmin={isAdmin ? () => navigate(`/${organizationSlug}/admin`) : undefined}
          onOpenProfile={handleOpenProfile}
        />
      </div>

      {/* Main Content */}
      <PullToRefresh onRefresh={async () => { await refreshEnrollments({ force: true }); }} className="h-full">
      <main className="pt-[calc(6rem+env(safe-area-inset-top))] sm:pt-[calc(6.5rem+env(safe-area-inset-top))] px-3 sm:px-4 lg:px-6 pb-8 sm:pb-12">
        <div className="max-w-7xl mx-auto">
          {/* Welcome Section - Show for unauthenticated users */}
          {!user && (
            <div className="mb-12 text-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                {organization.welcome_message || `Welcome to ${organization.name}`}
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto mb-8">
                {organization.about_text || `Join our exciting competitions and test your skills! Sign in or create an account to get started.`}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                <button
                  onClick={() => setShowAuthModal(true)}
                  className="bg-gradient-to-r from-theme-primary to-theme-secondary text-white px-8 py-3.5 rounded-xl font-semibold hover:opacity-90 transition-all shadow-lg transform hover:scale-105"
                >
                  Get Started
                </button>
                {(activeLmsCompetitions.length + activePredictorCompetitions.length + activeWorldcupCompetitions.length) > 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {/*Free to join •*/} {(activeLmsCompetitions.length + activePredictorCompetitions.length + activeWorldcupCompetitions.length)} active competition{(activeLmsCompetitions.length + activePredictorCompetitions.length + activeWorldcupCompetitions.length) !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="space-y-12">
            {/* My Competitions Dashboard - Shows enrolled and available competitions for logged in users.
                Renders immediately with internal skeleton while enrollment loads (SWR pattern). */}
            {user && (
              <>
                <MyCompetitionsDashboard
                  enrolledCompetitions={visibleEnrolledCompetitions}
                  availableCompetitions={visibleAvailableCompetitions}
                  loading={enrollmentLoading}
                  organizationSlug={organizationSlug!}
                  organizationName={organization.name}
                  publicStats={publicStats}
                  wcFirstKickoff={wcFirstKickoff}
                  firstGameweekDeadlines={enrollmentFirstGameweekDeadlines}
                />

                {/* Recently Completed Competitions */}
                {!recentlyCompletedLoading && recentlyCompletedCompetitions.length > 0 && (
                  <RecentlyCompletedSection
                    competitions={recentlyCompletedCompetitions}
                    organizationSlug={organizationSlug!}
                  />
                )}
              </>
            )}

            {/* Competition Types Info - Only show for non-authenticated users */}
            {!user && !competitionsLoading && (activeLmsCompetitions.length > 0 || activePredictorCompetitions.length > 0 || activeWorldcupCompetitions.length > 0) && (
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-6 text-center">
                  Active Competitions
                </h2>
                <div className="max-w-4xl mx-auto space-y-6">
                  {/* Competition Types Info */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {activeLmsCompetitions.length > 0 && (
                      <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-5 border border-green-500/30">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="inline-flex items-center justify-center w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg shadow-md">
                            <Target className="w-5 h-5 text-white" />
                          </div>
                          <h3 className="font-bold text-lg text-gray-900 dark:text-white">Last Man Standing</h3>
                        </div>
                        <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                          <li className="flex items-start">
                            <span className="text-green-500 mr-2 flex-shrink-0 font-bold">✓</span>
                            <span>Pick one winning team each gameweek</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-green-500 mr-2 flex-shrink-0 font-bold">✓</span>
                            <span>Can't pick the same team twice</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-green-500 mr-2 flex-shrink-0 font-bold">✓</span>
                            <span>Last person standing wins</span>
                          </li>
                        </ul>
                      </div>
                    )}
                    {activePredictorCompetitions.length > 0 && (
                      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-xl p-5 border border-blue-500/30">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="inline-flex items-center justify-center w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg shadow-md">
                            <Trophy className="w-5 h-5 text-white" />
                          </div>
                          <h3 className="font-bold text-lg text-gray-900 dark:text-white">Score Predictor</h3>
                        </div>
                        <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                          <li className="flex items-start">
                            <span className="text-blue-500 mr-2 flex-shrink-0 font-bold">✓</span>
                            <span>Predict exact scores for every match</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-blue-500 mr-2 flex-shrink-0 font-bold">✓</span>
                            <span>Earn points for correct predictions</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-blue-500 mr-2 flex-shrink-0 font-bold">✓</span>
                            <span>Top scorer wins the competition</span>
                          </li>
                        </ul>
                      </div>
                    )}
                    {activeWorldcupCompetitions.length > 0 && (
                      <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl p-5 border border-amber-500/30">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="inline-flex items-center justify-center w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg shadow-md">
                            <Globe className="w-5 h-5 text-white" />
                          </div>
                          <h3 className="font-bold text-lg text-gray-900 dark:text-white">World Cup 2026</h3>
                        </div>
                        <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                          <li className="flex items-start">
                            <span className="text-amber-500 mr-2 flex-shrink-0 font-bold">✓</span>
                            <span>Predict every match of the tournament</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-amber-500 mr-2 flex-shrink-0 font-bold">✓</span>
                            <span>Build your hypothetical bracket</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-amber-500 mr-2 flex-shrink-0 font-bold">✓</span>
                            <span>Compete for the top of the leaderboard</span>
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Competition Cards */}
                  <div className="space-y-3">
                    {activeLmsCompetitions.map((comp) => {
                      const stats = publicStats[comp.id];
                      return (
                        <PublicCompetitionCard
                          key={comp.id}
                          competition={comp}
                          onClick={() => setShowAuthModal(true)}
                          totalEntrants={stats?.total ?? 0}
                          remainingEntrants={stats?.remaining ?? null}
                          firstGameweekDeadline={publicFirstGameweekDeadlines[comp.id] ?? null}
                        />
                      );
                    })}
                    {activePredictorCompetitions.map((comp) => {
                      const stats = publicStats[comp.id];
                      return (
                        <PublicCompetitionCard
                          key={comp.id}
                          competition={comp}
                          onClick={() => setShowAuthModal(true)}
                          totalEntrants={stats?.total ?? 0}
                        />
                      );
                    })}
                    {activeWorldcupCompetitions.map((comp) => {
                      const stats = publicStats[comp.id];
                      return (
                        <PublicCompetitionCard
                          key={comp.id}
                          competition={comp}
                          onClick={() => setShowAuthModal(true)}
                          totalEntrants={stats?.total ?? 0}
                          wcFirstKickoff={wcFirstKickoff}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Failed to load competitions — surface the error instead of a silent empty state */}
            {!user && !competitionsLoading && competitionsError && (
              <div className="text-center py-4 md:py-10">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-2xl mb-4">
                  <AlertTriangle className="w-8 h-8 text-red-500 dark:text-red-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  Couldn't load competitions
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Something went wrong fetching active competitions. Please try again.
                </p>
                <button
                  onClick={() => refreshCompetitions()}
                  className="bg-gradient-to-r from-theme-primary to-theme-secondary text-white px-6 py-2.5 rounded-xl font-semibold hover:opacity-90 transition-all shadow-md"
                >
                  Retry
                </button>
              </div>
            )}

            {/* No Active Competitions - genuine empty state (no error, just nothing active) */}
            {!user &&
             !competitionsLoading &&
             !competitionsError &&
             activeLmsCompetitions.length === 0 &&
             activePredictorCompetitions.length === 0 &&
             activeWorldcupCompetitions.length === 0 && (
              <div className="text-center py-4 md:py-10">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-2xl mb-4">
                  <Trophy className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  No Active Competitions
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Check back soon for exciting new competitions!
                </p>
              </div>
            )}

            {/* Recently Completed for unauthenticated users */}
            {!user && !recentlyCompletedLoading && recentlyCompletedCompetitions.length > 0 && (
              <RecentlyCompletedSection
                competitions={recentlyCompletedCompetitions}
                organizationSlug={organizationSlug!}
              />
            )}
          </div>
        </div>
      </main>
      </PullToRefresh>

      {/* Auth Modal */}
      <AuthForm
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onAuthSuccess={() => {
          setShowAuthModal(false);
          // No reload — AuthContext will update `user`, triggering enrollment refetch
          // and admin role check via their dependency arrays. SPA state is preserved.
        }}
      />

      {/* PWA Install Banner */}
      <PWAInstallBanner 
        organizationName={organization.name}
        organizationLogoUrl={organization.logoUrl}
      />
    </div>
  );
}
